import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { LayoutGrid, Calculator, LogOut, LogIn, UserPlus } from "lucide-react";

const LangSwitch = () => {
  const { lang, setLang } = useApp();
  const langs = ["fr", "en", "de"];
  return (
    <div className="flex items-center gap-0 border border-slate-300" data-testid="lang-switch">
      {langs.map((l, i) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}`}
          className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border-l border-slate-300 ${
            lang === l ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

const NavLink = ({ to, children, testid, currentPath }) => {
  const active = currentPath === to;
  return (
    <Link
      to={to}
      data-testid={testid}
      className={`px-4 py-2 text-sm font-mono uppercase tracking-[0.15em] border-b-2 ${
        active ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"
      }`}
    >
      {children}
    </Link>
  );
};

const Layout = ({ children }) => {
  const { user, t, logout } = useApp();
  const loc = useLocation();
  const nav = useNavigate();
  const path = loc.pathname;

  return (
    <div className="app-root font-sans">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
            <div className="w-8 h-8 bg-slate-900 flex items-center justify-center">
              <span className="text-white font-serif text-lg leading-none">L</span>
            </div>
            <div>
              <div className="font-serif text-lg leading-none">Lux<span className="text-blue-700">·</span>Pension</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mt-1">Estimator · 2025</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/" testid="nav-calculator" currentPath={path}><Calculator size={14} strokeWidth={1.5} className="inline mr-2" />{t.nav.calculator}</NavLink>
            <NavLink to="/frontaliers" testid="nav-frontaliers" currentPath={path}>{t.nav.frontaliers}</NavLink>
            <NavLink to="/blog" testid="nav-blog" currentPath={path}>{t.nav.blog}</NavLink>
            {user && <NavLink to="/dashboard" testid="nav-dashboard" currentPath={path}><LayoutGrid size={14} strokeWidth={1.5} className="inline mr-2" />{t.nav.dashboard}</NavLink>}
          </nav>

          <div className="flex items-center gap-3">
            <LangSwitch />
            {user ? (
              <button
                onClick={async () => { await logout(); nav("/"); }}
                data-testid="logout-btn"
                className="px-4 py-2 text-xs font-mono uppercase tracking-[0.15em] border border-slate-300 hover:bg-slate-50"
              >
                <LogOut size={12} strokeWidth={1.5} className="inline mr-2" />{t.nav.logout}
              </button>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login" className="px-4 py-2 text-xs font-mono uppercase tracking-[0.15em] border border-slate-300 hover:bg-slate-50">
                  <LogIn size={12} strokeWidth={1.5} className="inline mr-2" />{t.nav.login}
                </Link>
                <Link to="/register" data-testid="nav-register" className="px-4 py-2 text-xs font-mono uppercase tracking-[0.15em] bg-slate-900 text-white hover:bg-slate-800">
                  <UserPlus size={12} strokeWidth={1.5} className="inline mr-2" />{t.nav.register}
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main>{children}</main>

      <footer className="border-t border-slate-200 mt-20 py-10 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8 flex flex-col md:flex-row justify-between items-start gap-6">
          <div>
            <div className="font-serif text-base text-slate-900">Lux·Pension</div>
            <div className="text-xs font-mono uppercase tracking-[0.15em] text-slate-500 mt-1">{t.footer}</div>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs font-mono uppercase tracking-[0.15em] text-slate-500">
            <Link to="/legal" data-testid="footer-legal" className="hover:text-slate-900">{t.nav.legal}</Link>
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-slate-900">{t.nav.privacy}</Link>
            <Link to="/blog" data-testid="footer-blog" className="hover:text-slate-900">{t.nav.blog}</Link>
            <Link to="/frontaliers" data-testid="footer-frontaliers" className="hover:text-slate-900">{t.nav.frontaliers}</Link>
          </div>
          <div className="text-xs font-mono text-slate-400">© 2025 — Lux·Pension</div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
